'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * İçerik sayfalarının açılışı — AÇIK zeminde sayfa başlığı.
 *
 * Anasayfanın hero'suyla akrabalığı yapıda değil ızgarada: aynı sütun, aynı
 * dikey hatlar, aynı yatay dolgu (26px / 30px). Ama koyu blok, yeşil rampa ve
 * dalga alanı burada YOK (kullanıcı kararı, 10 Ağu 2026) — sayfa açık zeminle
 * açılıyor ve anasayfanın ışığını ödünç almıyor.
 *
 * Header o yüzden `surface="light"` ile basılıyor: izleyeceği koyu blok
 * olmadığı için beyaz kabuk en baştan ayrık duruyor (gerekçe:
 * components/site-header.tsx).
 *
 * ── Neden sola dayalı ─────────────────────────────────────────────────────
 *
 * Anasayfa hero'su ortalanmış çünkü orada tek iş var: bir vaadi ilan etmek.
 * Burada okumaya başlanan uzun bir sayfa var; sola dayalı başlık gözün satır
 * başını bulduğu yeri sabitliyor ve altındaki bölümlerin hepsiyle aynı hatta
 * duruyor. Ortalanmış bir başlığın altına sola dayalı altı bölüm gelince
 * sayfa iki farklı ızgaradan konuşuyormuş gibi oluyor.
 *
 * ── Ölçü ──────────────────────────────────────────────────────────────────
 *
 * Başlık `--page-title` (28 → 46 → 56px): bölüm başlıklarından (22 → 37 →
 * 44px) belirgin biçimde büyük, anasayfa hero'sundan (29 → 48 → 60px) küçük.
 * Sayfada tek bir birinci ses var ve o anasayfada.
 *
 * Vurgu `<hl>` ile — sitedeki bütün başlıkların ortak aracı. Koyu sürümdeki
 * iki renkli gradient satırın karşılığı bu; açık zeminde gradient metin
 * okunurluğu düşürüyor, yeşil şerit ise kendi zeminini getiriyor.
 *
 * 🔑 Satır arası 1.34 olmak ZORUNDA: `.hl` şeridinin boyu lib/rich-text.tsx
 * içinde `LINE = 1.34` varsayımıyla hesaplanıyor. Daha sıkı bir satırda
 * sarılan iki şerit üst üste biniyor (ölçüldü: 1.18'de ~2px).
 *
 * ── Künye ─────────────────────────────────────────────────────────────────
 *
 * `<dl>` çünkü ad–değer çiftleri, dekoratif rozet değil. Ayraçlar hücrenin
 * İÇİNDE — `<dl>`nin doğrudan çocuğu yalnızca `<div>`, `<dt>`, `<dd>` olabilir.
 *
 * 🔴 Değerler YER TUTUCU: messages → `about._DEMO`.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Bölüm ekranın tepesinde açılıyor; `whileInView` beklemesi anlamsız, `animate`
 * ile doğrudan oynuyor. Sıra okuma sırası: etiket → başlık → cümle → künye.
 * Blur yalnızca başlıkta — göz oraya odaklanıyor, geri kalanı yerine oturuyor.
 * Sonsuz döngü ve parallax yok; kök layout'taki <MotionConfig
 * reducedMotion="user"> tamamını karşılıyor (DESIGN.md §1).
 */

/** Künye sırası. Anahtarlar messages → `about.head.facts` ile birebir. */
const FACTS = ['founded', 'base', 'focus'] as const;

const sceneV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose, delayChildren: 0.05 } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Bölümdeki tek blur: sayfanın adı. */
const titleV: Variants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

const factsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const factV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

/* Künyeyi sayfadan ayıran hat — soldan sağa çiziliyor. */
const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: {
    opacity: 1,
    scaleX: 1,
    transition: { duration: dur.slow, ease: ease.out },
  },
};

const edgeV: Variants = {
  hidden: { opacity: 0, scale: 0 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.default) },
};

export function PageHead({ ns }: { ns: string }) {
  const t = useTranslations(ns);
  const uid = useId();

  return (
    <section className="page-head" aria-labelledby={`${uid}-title`}>
      {/* Header `position: fixed` — akışta yer kaplamıyor, payı burada.
          Ayrık kabuk header kutusunun altına taşıyor (14 + 72 = 86px), bu
          yüzden pay --header-h değil kabuğun alt kenarından hesaplanıyor. */}
      <div className="page-head-space" aria-hidden="true" />

      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div
            className="page-head-frame"
            variants={sceneV}
            initial="hidden"
            animate="show"
          >
            <motion.p className="page-head-eyebrow" variants={itemV}>
              {t('eyebrow')}
            </motion.p>

            <motion.h1 className="page-head-title" id={`${uid}-title`} variants={titleV}>
              {t.rich('title', { hl })}
            </motion.h1>

            <motion.p className="page-head-lead" variants={itemV}>
              {t('lead')}
            </motion.p>

            {/* Hat `<dl>`nin İÇİNDE değil kardeşi: `<dl>`nin içerik modeli
                yalnızca dt/dd (ya da hepsi div olan gruplar) kabul ediyor,
                araya dekoratif bir span koymak geçersiz işaretleme demek.
                Varyantlar React bağlamıyla aktığı için düz `<dl>` zinciri
                kırmıyor — hücreler gecikmelerini yine alıyor. */}
            <motion.div className="page-head-facts" variants={factsV}>
              <motion.span
                className="page-head-facts-rule"
                aria-hidden="true"
                variants={ruleV}
              />
              <dl className="page-head-facts-list">
                {FACTS.map((key, i) => (
                  <motion.div className="page-head-fact" key={key} variants={factV}>
                    {/* İlk hücrenin solunda ayraç yok: orada çerçevenin kendi
                        hattı (≥600px) ya da künyeyi açan yatay hat var. */}
                    {i > 0 && (
                      <motion.span
                        className="page-head-fact-edge"
                        aria-hidden="true"
                        variants={edgeV}
                      />
                    )}
                    <dt className="page-head-fact-label">{t(`facts.${key}.label`)}</dt>
                    <dd className="page-head-fact-value">{t(`facts.${key}.value`)}</dd>
                  </motion.div>
                ))}
              </dl>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
